import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import Layout from "./frontend/include/Layout";
import Notify from "./components/Notify";
import ProtectedRoute from "./components/ProtectedRoute";
import PageLoader from "./components/PageLoader";
import ScrollToTop from "./components/ScrollToTop";

// Public frontend pages
const Login = lazy(() => import("./frontend/pages/Login"));
const Signup = lazy(() => import("./frontend/pages/Signup"));
const ForgotPassword = lazy(() => import("./frontend/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./frontend/pages/ResetPassword"));
const Home = lazy(() => import("./frontend/pages/Home"));
const PrivacyPolicy = lazy(() => import("./frontend/pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./frontend/pages/TermsOfService"));
const CookiePolicy = lazy(() => import("./frontend/pages/CookiePolicy"));
const RefundPolicy = lazy(() => import("./frontend/pages/RefundPolicy"));
const Contact = lazy(() => import("./frontend/pages/Contact"));
const About = lazy(() => import("./frontend/pages/About"));
const Pricing = lazy(() => import("./frontend/pages/Pricing"));
const CourseSearch = lazy(() => import("./frontend/pages/CourseSearch"));
const CourseMap = lazy(() => import("./frontend/pages/CourseMap"));
const CourseDetail = lazy(() => import("./frontend/pages/CourseDetail"));

// Provider pages
const ProviderBase = lazy(() => import("./provider/include/Base"));
const ProviderDashboard = lazy(() => import("./provider/pages/Dashboard"));
const ProviderEvents = lazy(() => import("./provider/pages/Events"));
const EventForm = lazy(() => import("./provider/pages/EventForm"));
const ProviderBookings = lazy(() => import("./provider/pages/Bookings"));
const BookingDetail = lazy(() => import("./provider/pages/BookingDetail"));
const ProviderProfile = lazy(() => import("./provider/pages/Profile"));
const ProviderWallet = lazy(() => import("./provider/pages/Wallet"));
const ProviderPayments = lazy(() => import("./provider/pages/Payments"));
const ProviderCertificates = lazy(() => import("./provider/pages/Certificates"));
const ProviderAttendance = lazy(() => import("./provider/pages/Attendance"));
const ProviderBulkEmail = lazy(() => import("./provider/pages/BulkEmail"));
const ProviderSettings = lazy(() => import("./provider/pages/Settings"));

// Admin pages
const AdminBase = lazy(() => import("./admin/include/Base"));
const AdminDashboard = lazy(() => import("./admin/pages/Dashboard"));
const AdminEvents = lazy(() => import("./admin/pages/Events"));
const AdminProviders = lazy(() => import("./admin/pages/Providers"));
const AdminCustomers = lazy(() => import("./admin/pages/Customers"));
const AdminBookings = lazy(() => import("./admin/pages/Bookings"));
const AdminPayments = lazy(() => import("./admin/pages/Payments"));
const AdminPayouts = lazy(() => import("./admin/pages/Payouts"));
const AdminRefunds = lazy(() => import("./admin/pages/Refunds"));
const AdminReviews = lazy(() => import("./admin/pages/Reviews"));
const AdminCoupons = lazy(() => import("./admin/pages/Coupons"));
const AdminCategories = lazy(() => import("./admin/pages/Categories"));
const AdminCommission = lazy(() => import("./admin/pages/Commission"));
const AdminReports = lazy(() => import("./admin/pages/Reports"));
const AdminBanners = lazy(() => import("./admin/pages/Banners"));
const AdminSettings = lazy(() => import("./admin/pages/Settings"));
const AdminContacts = lazy(() => import("./admin/pages/Contacts"));
const AdminProfile = lazy(() => import("./admin/pages/Profile"));

// Customer pages
const CustomerBase = lazy(() => import("./customer/include/Base"));
const CustomerProfile = lazy(() => import("./customer/pages/Profile"));
const UpdateProfile = lazy(() => import("./customer/pages/UpdateProfile"));
const CustomerDashboard = lazy(() => import("./customer/pages/Dashboard"));
const CustomerEvents = lazy(() => import("./customer/pages/Events"));
const EventDetail = lazy(() => import("./customer/pages/EventDetail"));
const CustomerBookings = lazy(() => import("./customer/pages/Bookings"));
const CustomerBookingDetail = lazy(() => import("./customer/pages/BookingDetail"));
const CustomerReviews = lazy(() => import("./customer/pages/Reviews"));
const CustomerCertificates = lazy(() => import("./customer/pages/Certificates"));
const CustomerPayments = lazy(() => import("./customer/pages/Payments"));

const App = () => {
  return (
    <AuthProvider>
      <Notify />
      <BrowserRouter>
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public frontend routes (with Header + Footer) */}
            <Route element={<Layout />}>
              <Route path="/" element={<Home />} />
              <Route path="/courses" element={<CourseSearch />} />
              <Route path="/courses/map" element={<CourseMap />} />
              <Route path="/maps" element={<Navigate to="/courses/map" replace />} />
              <Route path="/courses/:id" element={<CourseDetail />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/about"   element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms"   element={<TermsOfService />} />
              <Route path="/cookies" element={<CookiePolicy />} />
              <Route path="/refund-policy" element={<RefundPolicy />} />
              <Route path="/refund-terms"  element={<RefundPolicy />} />
              <Route path="/refunds"       element={<RefundPolicy />} />
            </Route>

            {/* Auth pages (no Header/Footer) */}
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* ── Provider routes (protected: provider only) ── */}
            <Route element={<ProtectedRoute role="provider" />}>
              <Route path="/provider" element={<ProviderBase />}>
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard"      element={<ProviderDashboard />} />
                <Route path="events"         element={<ProviderEvents />} />
                <Route path="events/create"  element={<EventForm />} />
                <Route path="events/:id/edit" element={<EventForm />} />
                <Route path="bookings"       element={<ProviderBookings />} />
                <Route path="bookings/:id"   element={<BookingDetail />} />
                <Route path="profile"        element={<ProviderProfile />} />
                <Route path="wallet"         element={<ProviderWallet />} />
                <Route path="revenue"        element={<ProviderPayments />} />
                <Route path="certificates"   element={<ProviderCertificates />} />
                <Route path="attendance"     element={<ProviderAttendance />} />
                <Route path="bulk-email"     element={<ProviderBulkEmail />} />
                <Route path="settings"       element={<ProviderSettings />} />
                <Route path="support"        element={<div className="p-6">Support</div>} />
                <Route path="*"              element={<Navigate to="dashboard" replace />} />
              </Route>
            </Route>

            <Route element={<ProtectedRoute role="admin" />}>
              <Route path="/admin" element={<AdminBase />}>
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard"  element={<AdminDashboard />} />
                <Route path="profile"    element={<AdminProfile />} />
                <Route path="events"     element={<AdminEvents />} />
                <Route path="providers"  element={<AdminProviders />} />
                <Route path="customers"  element={<AdminCustomers />} />
                <Route path="bookings"   element={<AdminBookings />} />
                <Route path="payments"   element={<AdminPayments />} />
                <Route path="payouts"    element={<AdminPayouts />} />
                <Route path="refunds"    element={<AdminRefunds />} />
                <Route path="reviews"    element={<AdminReviews />} />
                <Route path="coupons"    element={<AdminCoupons />} />
                <Route path="categories" element={<AdminCategories />} />
                <Route path="commission" element={<AdminCommission />} />
                <Route path="reports"    element={<AdminReports />} />
                <Route path="banners"    element={<AdminBanners />} />
                <Route path="contacts"  element={<AdminContacts />} />
                <Route path="settings"   element={<AdminSettings />} />
              </Route>
            </Route>

            {/* ── Customer routes (protected: customer only) ── */}
            <Route element={<ProtectedRoute role="customer" />}>
              <Route path="/customer" element={<CustomerBase />}>
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard"        element={<CustomerDashboard />} />
                <Route path="events"           element={<CustomerEvents />} />
                <Route path="events/:id"       element={<EventDetail />} />
                <Route path="bookings"         element={<CustomerBookings />} />
                <Route path="bookings/:id"     element={<CustomerBookingDetail />} />
                <Route path="profile"          element={<CustomerProfile />} />
                <Route path="profile/update"   element={<UpdateProfile />} />
                <Route path="reviews"          element={<CustomerReviews />} />
                <Route path="certificates"     element={<CustomerCertificates />} />
                <Route path="billing"          element={<CustomerPayments />} />
                <Route path="settings"         element={<div className="p-6">Settings — coming soon</div>} />
                <Route path="support"          element={<div className="p-6">Help & Support — coming soon</div>} />
                <Route path="*"                element={<Navigate to="dashboard" replace />} />
              </Route>
            </Route>
            {/* 404 catch-all */}
            <Route path="*" element={
              <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-slate-50">
                <p className="text-6xl font-black text-slate-200">404</p>
                <p className="text-lg font-semibold text-slate-600">Page not found</p>
                <a href="/" className="rounded-xl bg-gradient-to-r from-[#8A9778] to-[#6B7760] px-5 py-2.5 text-sm font-semibold text-white no-underline shadow-sm hover:opacity-90 transition-all">Go home</a>
              </div>
            } />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
};

export default App;
